'use client';

import React, { type FC, useCallback, useMemo } from 'react';
import { CartCouponForm } from '@kidslabs/module-flash-sale';
import { CartTotal, type AuthenticationGuardConfig } from '@kidslabs/module-quote';
import { type CartFooterProps } from './cart-footer.types';
import styles from './cart-footer.module.css';
import { useSession } from 'next-auth/react';
import { useModalAction } from '@/_application/core/components/ui/modal/modal.context';

/**
 * CartFooter - Cart footer component with coupon form and total
 * Matches mobile module-theme-hermes implementation
 */

export const CartFooter: FC<CartFooterProps> = ({ variant = 'Footer' }) => {
    const { status: sessionStatus } = useSession();
    const { openModal } = useModalAction();

    const isAuthenticated = sessionStatus === 'authenticated';
    const isLoading = sessionStatus === 'loading';

    const authenticationGuard: AuthenticationGuardConfig = useMemo(
        () => ({
            isAuthenticated,
            isLoading,
            openLoginModal: () => openModal('OTP_LOGIN'),
        }),
        [isAuthenticated, isLoading, openModal]
    );

    const renderCartTotal = useCallback(() => {
        return <CartTotal authenticationGuard={authenticationGuard} />;
    }, [authenticationGuard]);

    const renderCartCouponForm = useCallback(() => {
        if (!isAuthenticated) {
            return;
        }
        return <CartCouponForm />
    }, [isAuthenticated])

    if (variant === 'OrderSummaryAction') {
        return renderCartTotal();
    }

    return (
        <div className={styles.cartFooterContainer}>
            {renderCartCouponForm()}
            {renderCartTotal()}
        </div>
    );
};
